import { Clock } from "lucide-react";
import { EmptyState } from "@/ui/patterns";
import type { PartnerReservation } from "@/domain/reservation/types";
import { useReservationAnswers } from "@/features/reservations/hooks/useReservationAnswers";
import { CaptureIncidentBanner } from "./CaptureIncidentBanner";
import { ReservationQueueRow } from "./ReservationQueueRow";

interface ReservationQueueProps {
  queue: PartnerReservation[];
  isSearching?: boolean;
  title?: string;
}

export function ReservationQueue({
  queue,
  isSearching = false,
  title = "Čeká na potvrzení",
}: ReservationQueueProps) {
  const { confirm, reject, captureIncident, dismissCaptureIncident } = useReservationAnswers();
  const answeringId = confirm.isPending
    ? confirm.variables
    : reject.isPending
      ? reject.variables
      : null;

  const banner = captureIncident !== null && (
    <CaptureIncidentBanner reservationId={captureIncident} onDismiss={dismissCaptureIncident} />
  );

  // While a term is typed the screen answers for the whole search in one place,
  // so a queue with no match says nothing rather than contradicting it — but a
  // capture incident is not about the current search and must survive it.
  if (isSearching && queue.length === 0) return banner || null;

  return (
    <section className="space-y-4">
      {banner}
      <div className="flex items-baseline gap-3">
        <Clock className="h-4 w-4 text-primary" aria-hidden="true" />
        <h2 className="font-display text-2xl font-semibold tracking-tight">{title}</h2>
      </div>
      <p className="text-sm text-muted-foreground">
        Rezervace, u kterých zákazník čeká na vaši odpověď. Zatím se mu nic nestrhlo.
      </p>

      {queue.length === 0 ? (
        <div className="border border-dashed border-border rounded-xl">
          <EmptyState
            title="Nic nečeká na odpověď"
            description="Až někdo zarezervuje termín, který potvrzujete, objeví se tady — i s lhůtou, do kdy máte odpovědět."
          />
        </div>
      ) : (
        <ul className="divide-y divide-border border-y border-border">
          {queue.map((reservation) => (
            <ReservationQueueRow
              key={reservation.id}
              reservation={reservation}
              isAnswering={answeringId === reservation.id}
              onConfirm={confirm.mutate}
              onReject={(reservationId, options) => reject.mutate(reservationId, options)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
